import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  styled,
  Snackbar,
  Alert,
  InputAdornment,
  GlobalStyles,
  useTheme,
  useMediaQuery
} from '@mui/material';
import emailSendIcon from '../../../assets/images/email-send.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import backgroundImage from '../../../assets/images/ndc-bg.png';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import OtpModalContainer from '../NewsletterBanner/OtpModalContainer';
import OtpOptions from '../NewsletterBanner/OtpOptions';
import ErrorIcon from '@mui/icons-material/Error';
import { CustomBreadcrumbs } from '../../../components/DiscoverHeader/DiscoverHeader';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkEmailRequest,
  resetOtp,
  resetRedirectionAndOtp
} from '../../../redux/slices/userSlice';

const CustomButton = styled(Button)(({ theme }) => ({
  alignSelf: 'center',
  backgroundColor: '#1460AA',
  color: 'white',
  border: 'none',
  padding: '.75rem 2rem',
  cursor: 'pointer',
  fontFamily: "'Open Sans', sans-serif",
  fontSize: '1rem',
  borderRadius: '25px',
  fontWeight: 700,
  textTransform: 'none',
  transition:
    'background-color ease-in-out .15s, border-color ease-in-out .15s, box-shadow ease-in-out .15s',
  '&:hover': {
    backgroundColor: '#125a9a'
  },
  '&:disabled': {
    backgroundColor: '#ccc'
  }
}));

const NotificationPreferences = ({ logoHeight, announcementBanner }) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [submitEmail, setSubmitEmail] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [step, setStep] = useState('managePreferences');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<any>('success');
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Mobile devices

  const { redirection, otp, error } = useSelector((state: any) => state.user);

  const [isFocused, setIsFocused] = useState(false);
  const [value, setValue] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on component mount
  }, []);

  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    let isInvalid = !re.test(email);

    return isInvalid;
  };

  const handleInputChange = (e) => {
    const email = e.target.value;
    setValue(email);
    //setEmail(emailValue);

    // Validate email and enable/disable the button
    const isInvalid = validateEmail(email);
    setIsButtonDisabled(isInvalid);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(checkEmailRequest(value));
  };

  useEffect(() => {
    if (redirection) {
      navigate(`/${i18n.language}/seller-profile/user-settings`);
      dispatch(resetRedirectionAndOtp());
    } else if (otp) {
      setSubmitEmail(true);
      dispatch(resetRedirectionAndOtp());
    } else if (error) {
      setSnackbarMessage('failed to process your request');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirection, otp]);

  const data: any = t('managePreferences', {
    returnObjects: true,
    defaultValue: {}
  });

  const EmailStep = () => (
    <Box
      component="form"
      aria-labelledby="email-step-title"
      sx={{ m: '2.85rem 3.42rem 5.71rem 2.85rem' }}
      onSubmit={handleSubmit}
    >
      <Typography
        id="email-step-title"
        sx={{
          fontSize: '1.285rem',
          fontWeight: 600,
          lineHeight: '1.71rem',
          fontFamily: 'Open Sans',
          mb: '2.85rem'
        }}
      >
        {data.description}
      </Typography>
      <TextField
        value={value}
        sx={{
          width: isMobile ? '100%' : '50%', // Full width for mobile
          mb: isMobile ? '2rem' : '4.57rem' // Adjust margin for mobile
        }}
        onChange={handleInputChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(!!value)} // Keep label if there's a value
        label={isFocused || value ? data.email : ' '}
        placeholder={!isFocused && data.emailHelper} // Placeholder text
        variant="standard" // Standard variant for floating label behavior
        InputLabelProps={{
          sx: {
            fontSize: '12px',
            pl: isMobile ? '1.5rem' : '2.5rem', // Adjust padding for mobile
            color: '#717173', // Black color for the label
            '&.Mui-focused': {
              color: '#717173' // Gray color when focused
            }
          }
        }}
        InputProps={{
          sx: {
            '& .MuiInputBase-input': {
              fontSize: '16px',
              borderBottom: '1px solid #C9CACC', // Default border color
              '&::placeholder': {
                color: '#717173', // Placeholder color
                opacity: 1 // Ensure the placeholder is fully visible
              }
            },
            '&:after': {
              borderBottom: '1px solid #C9CACC' // Default border color
            },
            '&:hover:not(.Mui-disabled):before': {
              borderBottom: '1px solid #C9CACC' // Gray border on hover
            },
            '&.Mui-focused:before': {
              borderBottom: '1px solid #C9CACC' // Gray border when focused
            }
          },
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{ mt: isFocused ? '-1.25rem' : 'unset' }}
            >
              <img
                src={emailSendIcon}
                alt="email icon"
                style={{
                  width: isMobile ? '12px' : '16px', // Adjust icon size for mobile
                  height: isMobile ? '12px' : '16px'
                }}
              />
            </InputAdornment>
          )
        }}
        aria-required="true"
      />
      <br />
      <span
        id="email-validity-announcement"
        tabIndex={isButtonDisabled ? 0 : -1}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          margin: '-1px',
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          border: 0
        }}
        aria-live="polite"
        aria-hidden={!isButtonDisabled}
      >
        {data.ariaDisabledButton}
      </span>
       <CustomButton disabled={isButtonDisabled} type='submit' >{data.button}</CustomButton>
 
    </Box>
  );

  return <main
  className="center-width"
    role="main">
      <div style={{
      margin: `${logoHeight - (announcementBanner ? 79 : 62) + 16}px ${isMobile ? '1rem' : '6.85rem'} 4.57rem ${isMobile ? '1rem' : '6.85rem'}`

    }}>
    <GlobalStyles
      styles={{
        '.c-full-page': {
          minHeight: 'unset !important', // Override the height
        },
        '.MuiDivider-root': {
          margin: '0 !important',
        },
      }}
    />
    <CustomBreadcrumbs breadcrumb={data.title} />

      <Box
        sx={{
          bgcolor: 'background.paper',
          boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.15)',
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          outline: 0,
          marginTop: isMobile ? '2rem' : '3.2rem' // Adjust margin for mobile
        }}
      >
        <Box
          sx={{
            background: `url(${backgroundImage})`,
            height: isMobile ? '6rem' : '8rem', // Adjust height for mobile
            borderRadius: '1rem 1rem 0 0',
            backgroundRepeat: 'round',
            px: isMobile ? '1.5rem' : '2.5rem', // Adjust padding for mobile
            alignItems: 'center',
            display: 'flex'
          }}
        >
          <Typography
            id="otp-modal-title"
            component={'h1'}
            sx={{
              fontSize: isMobile ? '1.5rem' : '2.25rem', // Adjust font size for mobile
              fontWeight: 700,
              lineHeight: isMobile ? '2rem' : '3rem', // Adjust line height for mobile
              fontFamily: 'Open Sans'
            }}
          >
            {data.title}
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative'
          }}
        >
          {otpVerified ? (
            <OtpOptions
              step={step}
              setStep={setStep}
              email={value}
              setSnackbarMessage={setSnackbarMessage}
              setSnackbarOpen={setSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
              snackbarMessage={snackbarMessage}
              snackbarOpen={snackbarOpen}
              handleClose={() => {
                dispatch(resetOtp());
                navigate(`/${i18n.language}/ndc-program/home`);
              }}
            />
          ) : (
            EmailStep()
          )}

        {['error', 'warning'].includes(snackbarSeverity) ? (
          <Snackbar
            open={snackbarOpen}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              position: 'absolute',
              top: isMobile ? '0.5rem' : '1rem', // Adjust position for mobile
              right: isMobile ? '0.5rem' : '1rem', // Adjust position for mobile
            }}
          >
            <Alert
              onClose={() => setSnackbarOpen(false)}
              severity={snackbarSeverity}
              sx={{
                width: isMobile ? '90%' : '26rem', // Adjust width for mobile
                borderRadius: '1rem',
                border: snackbarSeverity === 'error' ? '0.5px solid #B83A32' :
              snackbarSeverity === 'warning' ? '0.5px solid #FFC247' :
              '0.5px solid #224D22',
                fontSize: '.875rem',
                fontWeight: 400,
                lineHeight: '1.25rem',
                bgcolor: snackbarSeverity === 'error' ? '#FFF1F0' :
                snackbarSeverity === 'warning' ? '#FFF9DD' :
                '#E8F5E9', // Default for success
              }}
              iconMapping={{
                error: <ErrorIcon htmlColor='#851109' />,
                success: <CheckCircleIcon />,
                warning: <ErrorIcon htmlColor='#EB9C00' />,
              }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        ) : null}
      </Box>
    </Box>
    {submitEmail && !otpVerified &&
      <OtpModalContainer
        open={true}
        handleClose={() => { 
          setSubmitEmail(false);
          dispatch(resetOtp());
        }}
        verficationCallback={setOtpVerified}
        email={value}
      />
    }
</div>

  </main >;

};

export default NotificationPreferences;
